/**
 * Funciones de utilidad para análisis y estadísticas de dashboards
 */

import { supabase } from './supabase'

export interface DashboardStats {
  total_views: number
  unique_views: number
  views_last_7_days: number
  views_last_30_days: number
  avg_views_per_day: number
}

export interface DailyTrend {
  date: string
  views: number
  unique_views: number
}

export interface DashboardRanking {
  id: string
  name: string
  slug: string
  total_views: number
  unique_views: number
  views_last_7_days: number
  views_last_30_days: number
  last_viewed_at: string | null
}

/**
 * Obtiene estadísticas completas de un dashboard
 */
export async function getDashboardStats(
  dashboardId: string
): Promise<DashboardStats | null> {
  try {
    // @ts-ignore - Supabase RPC types
    const { data, error } = await supabase.rpc('get_dashboard_stats', {
      dashboard_uuid: dashboardId,
    })

    if (error) {
      console.error('Error fetching dashboard stats:', error)
      return null
    }

    // La función RPC retorna un array con un solo elemento
    // @ts-ignore - Supabase RPC types
    return data && data.length > 0 ? data[0] : null
  } catch (err) {
    console.error('Failed to get dashboard stats:', err)
    return null
  }
}

/**
 * Obtiene la tendencia diaria de visitas de los últimos 30 días
 */
export async function getDashboardDailyTrend(
  dashboardId: string
): Promise<DailyTrend[]> {
  try {
    // @ts-ignore - Supabase RPC types
    const { data, error } = await supabase.rpc('get_dashboard_daily_trend', {
      dashboard_uuid: dashboardId,
    })

    if (error) {
      console.error('Error fetching dashboard trend:', error)
      return []
    }

    return data || []
  } catch (err) {
    console.error('Failed to get dashboard trend:', err)
    return []
  }
}

/**
 * Registra una visita a un dashboard
 */
export async function trackDashboardView(
  dashboardId: string,
  options?: {
    sessionId?: string
    userId?: string | null
    ipAddress?: string | null
    userAgent?: string | null
    referrer?: string | null
  }
): Promise<string | null> {
  try {
    // Generar o usar session_id
    let sessionId = options?.sessionId
    if (!sessionId) {
      sessionId = localStorage.getItem('dashboard_session_id') || undefined
      if (!sessionId) {
        sessionId = generateSessionId()
        localStorage.setItem('dashboard_session_id', sessionId)
      }
    }

    // @ts-ignore - Supabase RPC types
    const { data, error } = await supabase.rpc('track_dashboard_view', {
      p_dashboard_id: dashboardId,
      p_session_id: sessionId,
      p_user_id: options?.userId || undefined,
      p_ip_address: options?.ipAddress || undefined,
      p_user_agent: options?.userAgent || navigator.userAgent,
      p_referrer: options?.referrer || document.referrer || undefined,
    })

    if (error) {
      console.error('Error tracking dashboard view:', error)
      return null
    }

    return data
  } catch (err) {
    console.error('Failed to track dashboard view:', err)
    return null
  }
}

/**
 * Obtiene el ranking de dashboards por visitas
 */
export async function getDashboardRankings(): Promise<DashboardRanking[]> {
  try {
    const { data, error } = await supabase
      .from('mv_dashboard_rankings')
      .select('*')
      .order('total_views', { ascending: false })
      .limit(10)

    if (error) {
      console.error('Error fetching dashboard rankings:', error)
      return []
    }

    return data || []
  } catch (err) {
    console.error('Failed to get dashboard rankings:', err)
    return []
  }
}

/**
 * Obtiene todas las visitas de un dashboard (para análisis detallado)
 * Solo disponible para usuarios autenticados
 */
export async function getDashboardViews(
  dashboardId: string,
  options?: {
    limit?: number
    offset?: number
    startDate?: Date
    endDate?: Date
  }
): Promise<any[]> {
  try {
    let query = supabase
      .from('dashboard_views')
      .select('*')
      .eq('dashboard_id', dashboardId)
      .order('viewed_at', { ascending: false })

    if (options?.startDate) {
      query = query.gte('viewed_at', options.startDate.toISOString())
    }

    if (options?.endDate) {
      query = query.lte('viewed_at', options.endDate.toISOString())
    }

    if (options?.limit) {
      query = query.limit(options.limit)
    }

    if (options?.offset) {
      query = query.range(
        options.offset,
        options.offset + (options.limit || 10) - 1
      )
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching dashboard views:', error)
      return []
    }

    return data || []
  } catch (err) {
    console.error('Failed to get dashboard views:', err)
    return []
  }
}

/**
 * Genera un ID de sesión único
 */
function generateSessionId(): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 15)
  return `session_${timestamp}_${random}`
}

/**
 * Formatea un número de visitas para mostrar (ej: 1234 -> "1.2K")
 */
export function formatViewCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`
  } else {
    return count.toString()
  }
}

/**
 * Calcula el porcentaje de cambio entre dos valores
 */
export function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

/**
 * Obtiene el color para un porcentaje de cambio
 */
export function getChangeColor(percentage: number): string {
  if (percentage > 0) return 'text-green-600'
  if (percentage < 0) return 'text-red-600'
  return 'text-gray-600'
}

/**
 * Obtiene el ícono para un porcentaje de cambio
 */
export function getChangeIcon(percentage: number): string {
  if (percentage > 0) return '↑'
  if (percentage < 0) return '↓'
  return '→'
}
